import {
  addDays,
  eachDayOfInterval,
  endOfDay,
  format,
  isAfter,
  isBefore,
  isSameDay,
  startOfDay,
} from 'date-fns';
import { isBetween } from 'shared/lib/date-fns';
import {
  FrequencyUnit,
  HabitSchedule,
  PlannedOccurrence,
  ScheduleEntryState,
} from 'shared/types/habit-schedule';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { v4 as uuidv4 } from 'uuid';

import { getScheduleFrequency } from '../get-schedule-frequency';
import { getScheduleEndDate } from './get-schedule-end-date';
import { getScheduleTimeframesByDateRange } from './get-schedule-timeframe-by-date';

const dateFormat = 'yyyy-MM-dd';

export type Options = {
  startDate: Timestamp;
  endDate: Timestamp;
  today: Timestamp;
  weekStartsOn: WeekDays;
  uncompletedOnly?: boolean;
  sinceLastEntryOnly?: boolean;
};

export const getFrequencyPlanningBySchedule = (
  schedule: HabitSchedule,
  {
    startDate,
    endDate,
    today,
    weekStartsOn,
    uncompletedOnly,
    sinceLastEntryOnly,
  }: Options,
) => {
  // figure out where to start from
  const scheduleStartDate = startOfDay(schedule.startDate);
  const scheduleEnd = getScheduleEndDate(schedule);

  const _start = new Date(
    Math.max(
      // get the later date of the two; given startDate or schedule start date
      startDate.getTime(),
      scheduleStartDate.getTime(),
    ),
  );

  // figure out where to end
  const _end = new Date(
    Math.min(endDate.getTime(), scheduleEnd?.getTime() ?? endDate.getTime()),
  );

  // get the timeframes based on the start, end and frequency
  const frequency = getScheduleFrequency(schedule);
  const count = schedule.frequency?.count ?? 0;

  // When frequency is Month or Year, we want to prevent flooding the schedule with arbitrary dates.
  // Instead, we rely on `plannedOccurrences` (specific dates chosen by the user or system).
  // If no planned occurrences exist, we only suggest "today" if it falls within the schedule range.
  // This allows the user to see the task today and complete it, without cluttering future dates.
  // month and year schedules we only create entries for when we are sure there are more than the amount of days in that period
  if (
    (frequency === FrequencyUnit.Month && count < 31) ||
    (frequency === FrequencyUnit.Year && count < 365)
  ) {
    // when there are plannedOccurrences, we show them
    const filteredPlannedOccurrences =
      schedule.plannedOccurrences?.filter(({ date }) => {
        const dateEntry = schedule.entries.find(({ date: entryDate }) =>
          isSameDay(date, entryDate),
        );
        return (
          dateEntry?.state !== ScheduleEntryState.Skip &&
          dateEntry?.state !== ScheduleEntryState.Failure &&
          isBetween(date, _start, _end)
        );
      }) ?? [];

    if (filteredPlannedOccurrences.length) {
      return filteredPlannedOccurrences;
    }

    if (isBetween(today, _start, _end)) {
      // when no plannedOccurrences and today is between start and end date,
      // we create an entry for today with the schedule timeslot and the interval, as the user could work today on the habit.
      return [
        {
          id: uuidv4(),
          date: endOfDay(today),
          timeslot: schedule.timeslot ?? null,
        },
      ];
    }

    return [];
  }

  const { skippedDates, completedDates, allCompletedDates, lastEntryDate } =
    schedule.entries.reduce<{
      skippedDates: Set<string>;
      completedDates: Set<string>;
      allCompletedDates: Set<string>;
      lastEntryDate?: Timestamp;
    }>(
      (acc, entry) => {
        // get the skipped dates to exclude them from the plannedOccurrences
        if (
          entry.state === ScheduleEntryState.Skip ||
          entry.state === ScheduleEntryState.Failure
        ) {
          acc.skippedDates.add(format(entry.date, dateFormat));
        }

        // get the completed dates to exclude them from the plannedOccurrences
        if (entry.state === ScheduleEntryState.Complete && !!entry.value) {
          acc.allCompletedDates.add(format(entry.date, dateFormat));

          if (uncompletedOnly) {
            acc.completedDates.add(format(entry.date, dateFormat));
          }
        }

        if (
          sinceLastEntryOnly &&
          ((entry.state === ScheduleEntryState.Complete && !!entry.value) ||
            entry.state !== ScheduleEntryState.Complete)
        ) {
          acc.lastEntryDate =
            !acc.lastEntryDate ||
            acc.lastEntryDate.getTime() < entry.date.getTime()
              ? entry.date
              : acc.lastEntryDate;
        }

        return acc;
      },
      {
        skippedDates: new Set(),
        completedDates: new Set(),
        allCompletedDates: new Set(),
        lastEntryDate: undefined,
      },
    );

  // if there are timeframes from before the startDate (entries entered before the start of the schedule) we also take those
  const preTimeframes =
    _start.getTime() < scheduleStartDate.getTime()
      ? getScheduleTimeframesByDateRange(schedule, {
          startDate: _start,
          endDate: endOfDay(addDays(scheduleStartDate, -1)),
          weekStartsOn,
        })
      : [];
  const duringTimeframes = getScheduleTimeframesByDateRange(schedule, {
    startDate: scheduleStartDate,
    endDate: _end,
    weekStartsOn,
  });

  if (!preTimeframes.length) {
    // when there are no entries before the schedule startDate, the schedule startDate is the exact starting point
    duringTimeframes.forEach((timeframe) => {
      timeframe.startDate = new Date(
        Math.max(timeframe.startDate.getTime(), scheduleStartDate.getTime()),
      );
    });
  }
  // get a list of all unique timeframes
  const timeframes = [
    ...new Map(
      [...preTimeframes, ...duringTimeframes].map((t) => [
        `${t.startDate.getTime()}:${t.endDate.getTime()}`,
        t,
      ]),
    ).values(),
  ];

  // loop over timeframes
  return timeframes.reduce<PlannedOccurrence[]>((acc, timeframe) => {
    const plannedOccurrences =
      schedule.plannedOccurrences
        ?.filter(({ date }) =>
          isBetween(date, timeframe.startDate, timeframe.endDate),
        )
        .sort(
          (plannedA, plannedB) =>
            plannedA.date.getTime() - plannedB.date.getTime(),
        ) ?? [];

    if (plannedOccurrences.length) {
      acc.push(
        ...plannedOccurrences.filter(
          ({ date }) =>
            !skippedDates.has(format(date, dateFormat)) &&
            !completedDates.has(format(date, dateFormat)) &&
            (!lastEntryDate || !isBefore(date, lastEntryDate)) &&
            // occurrences from before the start date are not wanted
            !isBefore(date, startDate) &&
            // occurrences after the end date are not wanted
            !isAfter(date, _end),
        ),
      );

      return acc;
    }

    // if no planned occurrences, we create them with the schedule timeslot and the interval
    const daysInFrame = eachDayOfInterval({
      start: timeframe.startDate,
      end: timeframe.endDate,
    });

    const { occurrences } = daysInFrame.reduce<{
      occurrences: PlannedOccurrence[];
      foundCount: number;
    }>(
      (acc, date) => {
        // Take the first `count` days (these are the days targeted for completion)
        if (acc.foundCount >= count) {
          return acc;
        }

        const endOfDate = endOfDay(date);
        const dateStr = format(endOfDate, dateFormat);

        // Filter out skipped days (they don't count towards the frequency-count)
        if (skippedDates.has(dateStr)) {
          return acc;
        }

        // It is a valid day for the frequency count
        acc.foundCount++;

        const isCompleted = allCompletedDates.has(dateStr);

        // Usage validation
        // Filter to final occurrences based on completion/date validation
        if (
          (isCompleted && uncompletedOnly) || // Completed and we only want uncompleted
          // Before last entry (if sinceLastEntryOnly logic applies via lastEntryDate)
          (lastEntryDate && isBefore(endOfDate, lastEntryDate)) ||
          isBefore(endOfDate, startDate) || // Before request start
          isAfter(endOfDate, _end) // After request end
        ) {
          return acc;
        }

        // Map to final structure
        acc.occurrences.push({
          id: uuidv4(),
          date: endOfDate,
          timeslot: schedule.timeslot ?? null,
        });

        return acc;
      },
      { occurrences: [], foundCount: 0 },
    );

    acc.push(...occurrences);
    return acc;
  }, []);
};
